import {
  IPayableSchema,
  IScheduleDate
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import isEmpty from "lodash/isEmpty";
import { IPayeeRow } from "../../../services/search";
import { cleanCSVData } from "../../../shared/utils/csv";
import { calculateLineItemRegularPrice } from "../../utils";
import { lineItemFieldToCSVString } from "../../utils/invoicesCSVUtils";
import _ from "lodash";
import { toWSDateString } from "@wingspanhq/fe-component-library";

export type MapPayableToCSV = {
  payable: IPayableSchema;
  payeeRow?: IPayeeRow;
  payrollRun?: IScheduleDate;
};

// IPayableSchema doesn't expose `internal`, but the API returns it for any
// payable already attached to a payroll run (the same fields back the list
// filters in PayablesTable.tsx).
type PayableWithInternal = IPayableSchema & {
  internal?: {
    payroll?: { payrollId?: string };
    collaborators?: { parentInvoiceId?: string };
  };
};

// For standard payroll payables, the backend stamps depositedAt/estimatedDepositAt
// with the processing date rather than the contractor pay date, so we substitute
// the payable's dueDate (the expected contractor pay date) for the CSV export.
const selectorPayrollExpectedPayDate = (
  payable: IPayableSchema
): Date | undefined => {
  if (payable?.events?.instantPayoutAt) {
    return undefined;
  }

  const payrollId = (payable as PayableWithInternal)?.internal?.payroll
    ?.payrollId;
  return payrollId ? payable.dueDate : undefined;
};

const selectorReportDepositedDate = (
  payable: IPayableSchema
): Date | undefined => {
  const depositedDate =
    payable?.events?.instantPayoutAt || payable?.events?.depositedAt;
  const expectedPayDate = selectorPayrollExpectedPayDate(payable);

  if (
    depositedDate &&
    expectedPayDate &&
    depositedDate.getTime() < expectedPayDate.getTime()
  ) {
    return expectedPayDate;
  }

  return depositedDate;
};

const selectorReportEstimatedDepositDate = (
  payable: IPayableSchema
): Date | undefined =>
  selectorPayrollExpectedPayDate(payable) ||
  payable?.events?.estimatedDepositAt;

// For payables already attached to a payroll run (new or old stack), the
// caller's `payrollRun` describes the *current* upcoming run from the payer's
// payroll settings — not the historical run this payable went through — so
// we fall back to the contractor's expected pay date (the payable's dueDate).
// Only payables not yet attached to a payroll use the predicted payrollRun.
const selectorReportPayrollDepositDate = (
  payable: IPayableSchema,
  payrollRun?: IScheduleDate
): Date | undefined => {
  const internal = (payable as PayableWithInternal)?.internal;
  const hasPayrollRunAttached =
    !!internal?.payroll?.payrollId ||
    !!internal?.collaborators?.parentInvoiceId;

  return hasPayrollRunAttached ? payable.dueDate : payrollRun?.date;
};

const mapPayableTableItemData = (item: MapPayableToCSV) => {
  const { payable, payeeRow, payrollRun } = item;

  if (!payable) {
    return {};
  }

  const names =
    payeeRow && payeeRow.user
      ? wsName({
          user: payeeRow.user,
          member: payeeRow.member,
          payerOwnedData: payeeRow.payerOwnedData
        })
      : undefined;

  const payeeRowCompanyName =
    payeeRow?.payerOwnedData.payeeW9Data?.legalBusinessName ||
    payeeRow?.payeeOwnedData.payeeW9Data?.legalBusinessName ||
    payeeRow?.member.profile?.company?.legalBusinessName ||
    payeeRow?.member.profile?.company?.doingBusinessAs;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const reportedDepositedDate = selectorReportDepositedDate(payable);
  const reportedEstimatedDepositDate =
    selectorReportEstimatedDepositDate(payable);
  const reportedPayrollDepositDate = selectorReportPayrollDepositDate(
    payable,
    payrollRun
  );

  const itemData: Record<string, any> = {
    "External Contractor ID": payeeRow?.payerOwnedData.payeeExternalId,
    "Contractor Name": names?.getResolvedName(),
    "Contractor First Name": payeeRow?.user.profile?.firstName,
    "Contractor Last Name": payeeRow?.user.profile?.lastName,
    "Contractor Preferred Name": payeeRow?.user.profile?.preferredName,
    "Contractor Company Name": payeeRowCompanyName,
    "Contractor Email": payeeRow?.user.email,
    Amount: payable.amount,
    Currency: payable.currency?.toUpperCase(),
    "Purchase Order Number": payable.metadata?.purchaseOrderNumber,
    "Due Date": payable.dueDate
      ? toWSDateString(payable.dueDate, "input")
      : undefined,
    "Check Date": payable.client?.payDate
      ? toWSDateString(payable.client.payDate, "input")
      : undefined,
    "Payroll Deposit Date": reportedPayrollDepositDate
      ? toWSDateString(reportedPayrollDepositDate, "input")
      : undefined,
    "Created Date": payable.createdAt
      ? toWSDateString(payable.createdAt)
      : undefined,
    "Sent Date": payable?.events?.sentAt
      ? toWSDateString(payable.events.sentAt)
      : undefined,
    "Opened Date": payable?.events?.openedAt
      ? toWSDateString(payable.events.openedAt)
      : undefined,
    "Member Disputed Date": payable?.events?.disputedAt
      ? toWSDateString(payable.events.disputedAt)
      : undefined,
    "Invoice Declined Date": payable?.events?.clientDeclinedAt
      ? toWSDateString(payable.events.clientDeclinedAt)
      : undefined,
    // "Pre-Approved Date": TODO
    "Approved Date": payable?.events?.approvedAt
      ? toWSDateString(payable.events.approvedAt)
      : undefined,
    "Paid Date": payable?.events?.paidAt
      ? toWSDateString(payable.events.paidAt)
      : undefined,
    "Deposited Date": reportedDepositedDate
      ? toWSDateString(reportedDepositedDate)
      : undefined,
    "Estimated Deposit Date": reportedEstimatedDepositDate
      ? toWSDateString(reportedEstimatedDepositDate)
      : undefined,
    "Cancelled Date": payable?.events?.cancelledAt
      ? toWSDateString(payable.events.cancelledAt)
      : undefined,
    Timezone: timezone,
    "Invoice Number": payable.invoiceNumber,
    "Invoice Type": payable.labels?.invoiceType,
    "Member Workflow Status": payable.member?.workflowStatus,
    "Client Workflow Status": payable.client?.workflowStatus,
    Status: payable.status,
    // "Payroll Invoice Number": TODO
    // "Payroll Invoice Amount": TODO
    "Project Name": payable.labels?.projectName,
    "Invoice PDF": payable.attachments?.invoicePdf,
    "Receipt PDF": payable.attachments?.receiptPdf,
    "Invoice Link": payable.attachments?.invoiceLink,
    "Invoice Notes": payable.invoiceNotes,
    "Member Comment": payable.member?.comment,
    "Line Item Index": lineItemFieldToCSVString(
      (l, index) => index,
      payable.lineItems
    ),
    "Line Item Title": lineItemFieldToCSVString(
      l => l.description,
      payable.lineItems
    ),
    "Line Item Description": lineItemFieldToCSVString(
      l => l.detail,
      payable.lineItems
    ),
    "Line Item Amount": lineItemFieldToCSVString(
      l => calculateLineItemRegularPrice(l)?.toFixed(2),
      payable.lineItems
    ),
    ...(isEmpty(payable.labels)
      ? {}
      : Object.keys(payable.labels as any)
          .filter(key => !["createdBy"].includes(key))
          .reduce((acc, key) => {
            return {
              ...acc,
              [`Custom Field ${key}`]: payable.labels[key]
            };
          }, {}))
  };

  // Helper function for mapping line item labels to csv data
  const mapLabelsToCustomFields = (
    lineItemNumber: number,
    labels: Record<string, string | boolean | null>
  ) => {
    Object.keys(labels).forEach(labelKey => {
      const header = `Line Item ${lineItemNumber + 1}. ${_.startCase(
        labelKey
      )}`;
      itemData[header] = labels[labelKey];
    });
  };
  // Iterate over line items custom fields and add as additional columns
  payable.lineItems.forEach((lineItem: any, index: number) => {
    if (lineItem.labelsBak2) {
      mapLabelsToCustomFields(index, lineItem.labelsBak2);
    }
    if (lineItem.labels) {
      mapLabelsToCustomFields(index, lineItem.labels);
    }
  });

  return itemData;
};
export const mapPayablesTableToCSV = (data: Array<MapPayableToCSV>) => {
  let cleanResult = [];

  const dirtyResult = data
    .map(d => {
      try {
        return mapPayableTableItemData(d);
      } catch (error) {
        console.error(error);
      }
      return {};
    })
    .filter(d => !isEmpty(d));
  cleanResult = dirtyResult.map(d =>
    cleanCSVData(d, dirtyResult)
  ) as Array<any>;

  return cleanResult;
};
